/* eslint-disable complexity */
import { batch } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { IPayResponse, pay } from '@r1-frontend/api-domru/api-loyalty/v1/program/pay';
import { IResponse, seasonTicketActivate } from '@r1-frontend/api-domru/api-season-ticket/v1/season-ticket-activate';

import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import { getTicketDataLayerLabel, pushDataLayer } from '~/src/features/payments-abn/components/SeasonTicketPayment/helpers';
import {
    EValidityStatus,
    setBonusesResult,
    setConnectionResult,
    setInProcess,
    setTicketPopup,
} from '~/src/features/payments-abn/components/SeasonTicketPayment/store/actionCreators';
import { ddmm } from '~/src/helpers/date';
import { toCurrency } from '~/src/helpers/numbers';
import { TAppThunkDispatch, TGetState } from '~/src/store';
import { getClientPayment } from '~/src/store/actions/auth/getClientPayment';

import paySeasonTicket from './paySeasonTicket';

const month = getPluralize(monthPluralize);

const connectSeasonTicket = () => {
    return async(dispatch: TAppThunkDispatch, getState: TGetState): Promise<void> => {
        const state = getState();

        const {
            city: { provider: { providerId } },
            payment: { isSaveCard },
            seasonTicketPayment: { bonusesInfo, seasonTicket, result: connectionResult },
            wholesaleCheaperData: { wholesaleCheaperData },
        } = state;

        const { currentFlag = [] } = wholesaleCheaperData || {};

        if (currentFlag.length) {
            pushDataLayer('subscription_extend');
        }

        const {
            id: flagId = 0,
            activateDate = '',
            countMonth = 3,
            deltaPeriod = 0,
            paySum = 0,
            sumPrice = 0,
        } = seasonTicket || {};

        // если ранее не было успешных попыток подключения абонемента
        if (connectionResult !== 'success') {
            dispatch(setInProcess(true));

            const data = {
                id: flagId,
                paySum,
            };

            const connectionResponse = await doRequest.apiSeasonTicket<IResponse>(seasonTicketActivate(providerId, data));

            if (connectionResponse.isSuccess) {
                dispatch(setConnectionResult('success'));
                // показываем попап только в том случае, если суммы на балансе достаточно для активации абонемента
                if (!paySum) {
                    pushDataLayer('subscription_and_payment_success', getTicketDataLayerLabel({ countMonth, sumPrice, deltaPeriod }));

                    batch(() => {
                        dispatch(setInProcess(false));
                        dispatch(setTicketPopup({
                            isOpen: true,
                            step: 'success',
                            title: `Вы подключили абонемент на ${countMonth} ${month(countMonth)}`,
                            text: `Абонемент начнёт действовать ${ddmm(activateDate)}`,
                            type: EResultPopupStatus.SUCCESS,
                        }));
                    });

                    return;
                }
            } else {
                // при ошибке подключения не выполняем списание бонусов / оплату
                batch(() => {
                    dispatch(setConnectionResult('error'));
                    dispatch(setInProcess(false));
                    dispatch(setTicketPopup({
                        isOpen: true,
                        step: 'error',
                        title: 'Что-то пошло не так',
                        type: EResultPopupStatus.ERROR,
                    }));
                });
                pushDataLayer('subscription_error', getTicketDataLayerLabel({ countMonth, sumPrice, deltaPeriod }));

                return;
            }
        }

        const { isUseBonuses, amount: bonusAmount, validity, result } = bonusesInfo;

        if (isUseBonuses && validity === EValidityStatus.IS_VALID && result !== 'success') {
            dispatch(setInProcess(true));

            const { isSuccess } = await doRequest.apiLoyalty<IPayResponse>(
                pay(providerId, { bonusValue: bonusAmount }),
            );

            if (isSuccess) {
                // @ts-ignore
                dispatch(getClientPayment());
            }

            dispatch(setBonusesResult(isSuccess ? 'success' : 'error'));
            // оплачивает абонемент только бонусами
            if (bonusAmount === Math.ceil(paySum) && !isSaveCard) {
                pushDataLayer(
                    isSuccess ? 'subscription_and_payment_success' : 'subscription_connect_success',
                    getTicketDataLayerLabel({ countMonth, sumPrice, deltaPeriod }),
                );

                const title = isSuccess
                    ? `Вы пополнили баланс на ${toCurrency(bonusAmount)} ${RubleSymbol} и подключили абонемент на ${countMonth} ${month(countMonth)}`
                    : 'Вы подключили абонемент, но пополнить баланс не получилось';
                const text = isSuccess ? `Абонемент начнёт действовать ${ddmm(activateDate)}` : '';

                batch(() => {
                    dispatch(setInProcess(false));
                    dispatch(setTicketPopup({
                        isOpen: true,
                        step: isSuccess ? 'paid' : 'declined',
                        title,
                        text,
                        type: isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.WARNING,
                    }));
                });

                return;
            } else {
                batch(() => {
                    dispatch(setInProcess(false));
                    dispatch(paySeasonTicket());
                });

                return;
            }
        }

        dispatch(paySeasonTicket());
    };
};

export default connectSeasonTicket;
